import { Outlet, createBrowserRouter } from 'react-router';

import {
  AnonymousGuard,
  AuthGate,
  RequireAuth,
  RequireRole,
  RootRedirect,
  hasNotAllowedRole,
  isManager,
  isNewUser,
  isStoreManager,
  LoginPage,
} from '~domains/auth';
import { AppChatWidget } from '~domains/chat';
import {
  PartnerApplicationCreatePage,
  PartnerApplicationEditPage,
  PartnerApplicationMenuPage,
  PartnerApplicationSignPage,
} from '~domains/partner-application';
import { EmployeeProfilePage, ProfilePage } from '~domains/profile';

import { AccessDeniedPage } from '~pages/access-denied';
import { DashboardPage } from '~pages/dashboard';
import { LoansPage } from '~pages/loans';
import { CreateOrderPage, OrdersPage } from '~pages/orders';
import { PaymentsPage } from '~pages/payments';
import { TransactionsPage } from '~pages/transactions';

import { BusinessPagesLayout } from '~widgets/business-pages-layout';

import * as Sentry from '@breeze/web-lib-sentry';

import { PartnerRedirect } from './partner-redirect';
import { ProfileStateGuard } from './profile-state-guard';

const createBrowserRouterWithSentry =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

const routes = [
  {
    element: <AuthGate />,
    children: [
      {
        path: '*',
        element: <RootRedirect />,
      },
      {
        path: 'login',
        element: (
          <AnonymousGuard>
            <LoginPage />
          </AnonymousGuard>
        ),
      },
      {
        element: (
          <RequireAuth>
            <Outlet />
            <AppChatWidget />
          </RequireAuth>
        ),
        children: [
          {
            path: 'access-denied',
            element: (
              <RequireRole roles={[hasNotAllowedRole]}>
                <AccessDeniedPage />
              </RequireRole>
            ),
          },
          {
            path: 'partner',
            element: (
              <RequireRole roles={[isNewUser]}>
                <Outlet />
              </RequireRole>
            ),
            children: [
              {
                index: true,
                element: <PartnerRedirect />,
              },
              {
                path: 'profile',
                element: (
                  <ProfileStateGuard shouldBeFilled={false}>
                    <ProfilePage />
                  </ProfileStateGuard>
                ),
              },
              {
                element: (
                  <ProfileStateGuard shouldBeFilled={true}>
                    <Outlet />
                  </ProfileStateGuard>
                ),
                children: [
                  {
                    path: 'application',
                    element: <PartnerApplicationCreatePage />,
                  },
                  {
                    path: 'application/menu',
                    element: <PartnerApplicationMenuPage />,
                  },
                  {
                    path: 'application/:id',
                    element: <PartnerApplicationEditPage />,
                  },
                  {
                    path: 'application/:id/sign',
                    element: <PartnerApplicationSignPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'profile',
            element: <EmployeeProfilePage />,
          },
          {
            element: (
              <RequireRole roles={[isStoreManager, isManager]}>
                <BusinessPagesLayout />
              </RequireRole>
            ),
            children: [
              {
                path: 'dashboard',
                element: <DashboardPage />,
              },
              {
                path: 'transactions',
                element: <TransactionsPage />,
                children: [
                  {
                    path: 'loans',
                    element: <LoansPage />,
                  },
                  {
                    path: 'payments',
                    element: <PaymentsPage />,
                  },
                ],
              },
              {
                path: 'orders',
                element: <OrdersPage />,
              },
            ],
          },
          {
            path: 'orders/create',
            element: (
              <RequireRole roles={[isStoreManager, isManager]}>
                <CreateOrderPage />
              </RequireRole>
            ),
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouterWithSentry(routes, { basename });
